// RITA-1525 (Teilpaket 2) — Zustand des Vertragseditors: Dokument, Auswahl, aktiver Bereich,
// ausstehendes Format (Fett vor dem Tippen) und Verlauf. Alle Änderungen laufen über reine
// Operationen aus model/bearbeiten.ts; der Reducer nimmt den Zustand davor in den Verlauf auf.
// Der Bereich (Text, Kopf- oder Fußzeile) folgt immer der Auswahl, damit der Caret nie in einer
// nicht editierbaren Insel landet – auch nach Rückgängig/Wiederholen.
import { MutableRefObject, useCallback, useMemo, useReducer, useRef } from 'react';
import {
  Bearbeitungsergebnis,
  bereichBearbeitbarMachen,
  blockTeilen,
  einfuegeauswahl,
  einfuegeformat,
  formatAnAuswahl,
  Formatmerkmal,
  formatUmschalten,
  groesseAnAuswahl,
  groesseSetzen,
  kaestchenUmschalten,
  knotenWieTextEinfuegen,
  leerenBereichAufraeumen,
  listeUmschalten,
  loescheAuswahl,
  loescheRueckwaerts,
  loescheVorwaerts,
  mitEinfuegeformat,
  normalisiereDokument,
  seitenumbruchEinfuegen,
  seitenumbruchEntfernen,
  textEinfuegen,
  zeilenumbruchEinfuegen,
} from '../model/bearbeiten';
import { kaestchen, platzhalter } from '../model/erzeugen';
import { Textsegment, textInSegmente } from '../model/platzhalterText';
import {
  anfangVon,
  Auswahl,
  auswahlenGleich,
  auswahlKlemmen,
  Bereich,
  endeVon,
  formateGleich,
  Formatmerkmale,
  geerbtesFormatVorPosition,
  istKollabiert,
  kollabiert,
  mitGroesse,
  OHNE_FORMAT,
} from '../model/positionen';
import { absatzabstandEmVon } from '../model/abstaende';
import { eigeneGroesse, grundgroesseVon } from '../model/schriftgroessen';
import { HorizontalePosition, Inlineknoten, Logoeinstellung, Vertragsdokument } from '../model/types';
import {
  aufnehmen,
  kannRueckgaengig,
  kannWiederholen,
  leererVerlauf,
  rueckgaengig,
  Schrittart,
  Verlauf,
  wiederholen,
  Zustand,
} from '../model/verlauf';

export interface Editorzustand {
  dokument: Vertragsdokument;
  auswahl: Auswahl | null;
  bereich: Bereich;
  aktiveSeite: number;
  ausstehendesFormat: Formatmerkmale | null;
  verlauf: Verlauf<Verlaufszustand>;
  /** Vorläufige Browserkomposition: der vollständige Ausgangsstand bleibt bis zur Bestätigung erhalten. */
  komposition: { vorher: Editorzustand } | null;
  /** Letzte Auswahl im Text, wird beim Verlassen von Kopf-/Fußzeile wiederhergestellt. */
  letzteTextauswahl: Auswahl | null;
  /** Erzwingt den Neuaufbau aller Seiten (nach einer Komposition, die das DOM selbst verändert hat). */
  renderVersion: number;
  /** Zähler; bei Änderung holt sich der Host Fokus und Auswahl (nach Werkzeugleisten-Aktionen). */
  fokusAnforderung: number;
}

/**
 * Was ein Verlaufsschritt festhält: Dokument, Auswahl, ausstehendes Format und gemerkte Textstelle. Die gemerkte Stelle
 * muss zum Vertragstext des Schritts passen: Der Wechsel aus dem Text in Kopf-/Fußzeile und
 * „Seitenumbruch entfernen“ bei aktiver Kopf-/Fußzeile setzen sie, Rückgängig/Wiederholen tauschen
 * das Dokument aus. Ohne Mitführung führt die Rückkehr in den Text an eine veraltete oder entfallene
 * Stelle (Merkposten V6, Nachprüfung zum Kollegenreview !751).
 */
interface Verlaufszustand extends Zustand {
  letzteTextauswahl: Auswahl | null;
  ausstehendesFormat: Formatmerkmale | null;
}

function schnappschuss(zustand: Editorzustand): Verlaufszustand {
  return {
    dokument: zustand.dokument,
    auswahl: zustand.auswahl,
    letzteTextauswahl: zustand.letzteTextauswahl,
    ausstehendesFormat: zustand.ausstehendesFormat,
  };
}

/**
 * Ergebnis einer Operation im Editor: wie im Modell, optional mit einem ausstehenden Format für die nächste
 * Eingabe, wenn die neue Schreibstelle selbst keinen Formatträger hat (`formatWeiterfuehren`).
 */
export interface Operationsergebnis extends Bearbeitungsergebnis {
  ausstehendesFormat?: Formatmerkmale;
}

export type Operation = (
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  ausstehend: Formatmerkmale | null,
) => Operationsergebnis;

export type Editoraktion =
  | { typ: 'auswahl'; auswahl: Auswahl | null }
  | {
      typ: 'operation';
      art: Schrittart;
      ausfuehren: Operation;
      zeitMs: number;
      fokus?: boolean;
      neuAufbau?: boolean;
      /** Mit dem vorherigen Verlaufsschritt verschmelzen (Komposition über einer Auswahl). */
      verschmelzen?: boolean;
    }
  | {
      typ: 'dokument';
      art: Schrittart;
      dokument: Vertragsdokument;
      auswahl?: Auswahl | null;
      zeitMs: number;
      fokus?: boolean;
    }
  | { typ: 'kompositionStart'; auswahl: Auswahl | null }
  | { typ: 'kompositionEnde'; text: string; zeitMs: number }
  | { typ: 'rueckgaengig' }
  | { typ: 'wiederholen' }
  | { typ: 'bereich'; bereich: Bereich; seite: number; auswahl?: Auswahl | null; fokus?: boolean }
  | { typ: 'format'; merkmal: Formatmerkmal; zeitMs: number }
  /**
   * Schriftgröße in Punkt (RITA-1534): auf der Markierung bzw. als ausstehendes Format an der Schreibmarke.
   * `fokus` holt die Schreibmarke zurück – nötig, weil das Auswahlfeld (anders als die Knöpfe) den Fokus
   * nimmt; so wird auch die durch den Seitenneuaufbau kollabierte Browserauswahl sofort nachgeführt.
   */
  | { typ: 'groesse'; groessePt: number; zeitMs: number; fokus?: boolean }
  /** Holt Fokus und Schreibmarke in den Vertrag zurück, ohne etwas zu ändern (nach Menü oder Fenster). */
  | { typ: 'fokus' }
  | { typ: 'neuAufbauen' }
  | { typ: 'zuruecksetzen'; dokument: Vertragsdokument };

export function anfangszustand(dokument: Vertragsdokument): Editorzustand {
  return {
    dokument: normalisiereDokument(dokument),
    auswahl: null,
    bereich: 'text',
    aktiveSeite: 1,
    ausstehendesFormat: null,
    verlauf: leererVerlauf<Verlaufszustand>(),
    komposition: null,
    letzteTextauswahl: null,
    renderVersion: 0,
    fokusAnforderung: 0,
  };
}

function mitFokus(zustand: Editorzustand, fokus: boolean | undefined): Editorzustand {
  return fokus ? { ...zustand, fokusAnforderung: zustand.fokusAnforderung + 1 } : zustand;
}

/** Nach jeder Änderung: der aktive Kopf-/Fußbereich hat immer einen (ggf. leeren) Absatz für den Caret. */
function bereichSichern(dokument: Vertragsdokument, bereich: Bereich): Vertragsdokument {
  return bereich === 'text' ? dokument : bereichBearbeitbarMachen(dokument, bereich);
}

function bereichVon(auswahl: Auswahl | null, sonst: Bereich): Bereich {
  return auswahl ? auswahl.anker.bereich : sonst;
}

function verlaufsschritt(
  zustand: Editorzustand,
  schritt: { verlauf: Verlauf<Verlaufszustand>; zustand: Verlaufszustand },
): Editorzustand {
  const bereich = bereichVon(schritt.zustand.auswahl, zustand.bereich);
  const dokument = bereichSichern(schritt.zustand.dokument, bereich);
  // Ändert der Schritt den Vertragstext, gelten Schreibmarke und gemerkte Textstelle aus dem Verlauf.
  // Bleibt der Vertragstext derselbe (reine Kopf-/Fußzeilen-Änderung, Logo, Seitenzahl), bleibt die
  // zuletzt im Text gewählte Stelle erhalten (Entscheidung Julia 29.09.2026): als gemerkte Stelle
  // und – führt der Schritt in den Text, weil Rückgängig/Wiederholen zuvor dort ausgelöst wurde –
  // auch als Schreibmarke. Der Vergleich über die Identität genügt: Operationen außerhalb des Textes
  // reichen dessen Blockliste unverändert weiter.
  const textGeaendert = schritt.zustand.dokument.text !== zustand.dokument.text;
  const zuletztImText = zustand.bereich === 'text' ? zustand.auswahl : zustand.letzteTextauswahl;
  const ausVerlauf = schritt.zustand.auswahl && auswahlKlemmen(dokument, schritt.zustand.auswahl);
  const auswahl =
    !textGeaendert && bereich === 'text' && zuletztImText && zuletztImText.anker.bereich === 'text'
      ? auswahlKlemmen(dokument, zuletztImText)
      : ausVerlauf;
  return {
    ...zustand,
    dokument,
    auswahl,
    bereich,
    letzteTextauswahl: textGeaendert ? schritt.zustand.letzteTextauswahl : zuletztImText,
    verlauf: schritt.verlauf,
    // RITA-1550: Die unbenutzte Formatwahl gehört zum Verlauf, aber nur an die zugehörige Stelle.
    // Die V6-Regel darf eine später gesetzte Textauswahl erhalten; dorthin wandert kein altes Format.
    ausstehendesFormat: auswahlenGleich(auswahl, schritt.zustand.auswahl) ? schritt.zustand.ausstehendesFormat : null,
    fokusAnforderung: zustand.fokusAnforderung + 1,
  };
}

/** RITA-1550, Variante b: jede geänderte Formatwahl ist ein eigener Rückgängig-Schritt. */
function formatwahlAufnehmen(zustand: Editorzustand, format: Formatmerkmale, zeitMs: number): Editorzustand {
  if (!zustand.auswahl) return zustand;
  const bisher = zustand.ausstehendesFormat ?? formatAnAuswahl(zustand.dokument, zustand.auswahl);
  if (formateGleich(bisher, format)) return zustand;
  return {
    ...zustand,
    ausstehendesFormat: format,
    verlauf: aufnehmen(zustand.verlauf, schnappschuss(zustand), 'sonstiges', zeitMs, zustand.auswahl),
  };
}

/** Kein Undo: Abbrechen darf weder Redo erzeugen noch vorhandene Schritte verlieren. */
function kompositionVerwerfen(zustand: Editorzustand): Editorzustand {
  return {
    ...(zustand.komposition?.vorher ?? zustand),
    komposition: null,
    renderVersion: zustand.renderVersion + 1,
    fokusAnforderung: zustand.fokusAnforderung + 1,
  };
}

export function editorReducer(zustand: Editorzustand, aktion: Editoraktion): Editorzustand {
  // Ein anderer Bearbeitungsbefehl beendet zuerst die vorläufige Eingabe. So kann z. B. „Fett“
  // auf der ursprünglichen Markierung arbeiten; ein verspätetes compositionend nimmt ihn nicht zurück.
  if (
    zustand.komposition &&
    aktion.typ !== 'kompositionStart' &&
    aktion.typ !== 'kompositionEnde' &&
    aktion.typ !== 'auswahl' &&
    aktion.typ !== 'fokus' &&
    aktion.typ !== 'neuAufbauen'
  ) {
    return editorReducer(kompositionVerwerfen(zustand), aktion);
  }
  switch (aktion.typ) {
    case 'kompositionStart': {
      const basis = zustand.komposition ? kompositionVerwerfen(zustand) : zustand;
      const vorher = { ...basis, auswahl: aktion.auswahl };
      // Wie beim Tippen ent-hängt (V11, RITA-1533): Eine mitmarkierte Leerzeile bleibt stehen.
      const ergebnis = aktion.auswahl
        ? loescheAuswahl(basis.dokument, einfuegeauswahl(basis.dokument, aktion.auswahl))
        : null;
      return {
        ...vorher,
        dokument: ergebnis?.dokument ?? vorher.dokument,
        auswahl: ergebnis?.auswahl ?? vorher.auswahl,
        komposition: { vorher },
      };
    }
    case 'kompositionEnde': {
      const vorher = zustand.komposition?.vorher;
      // Auch ein verspätetes Ende nach einem Werkzeugleistenbefehl räumt Browserreste auf.
      if (!vorher || aktion.text === '' || !zustand.auswahl) return kompositionVerwerfen(zustand);
      const ergebnis = textEinfuegen(
        zustand.dokument,
        zustand.auswahl,
        aktion.text,
        vorher.ausstehendesFormat ?? undefined,
      );
      return {
        ...vorher,
        dokument: bereichSichern(ergebnis.dokument, vorher.bereich),
        auswahl: ergebnis.auswahl,
        ausstehendesFormat: null,
        komposition: null,
        verlauf: aufnehmen(vorher.verlauf, schnappschuss(vorher), 'tippen', aktion.zeitMs, ergebnis.auswahl),
        renderVersion: zustand.renderVersion + 1,
        fokusAnforderung: zustand.fokusAnforderung + 1,
      };
    }
    case 'auswahl': {
      if (zustand.komposition) return zustand;
      if (auswahlenGleich(zustand.auswahl, aktion.auswahl)) return zustand;
      return { ...zustand, auswahl: aktion.auswahl, ausstehendesFormat: null };
    }
    case 'operation': {
      if (!zustand.auswahl) return zustand;
      const ergebnis = aktion.ausfuehren(zustand.dokument, zustand.auswahl, zustand.ausstehendesFormat);
      const renderVersion = aktion.neuAufbau ? zustand.renderVersion + 1 : zustand.renderVersion;
      if (ergebnis.dokument === zustand.dokument) {
        const auswahl = auswahlenGleich(ergebnis.auswahl, zustand.auswahl) ? zustand.auswahl : ergebnis.auswahl;
        return mitFokus({ ...zustand, auswahl, renderVersion }, aktion.fokus);
      }
      const verlauf = aufnehmen(
        zustand.verlauf,
        schnappschuss(zustand),
        aktion.art,
        aktion.zeitMs,
        ergebnis.auswahl,
        aktion.verschmelzen,
      );
      return mitFokus(
        {
          ...zustand,
          dokument: bereichSichern(ergebnis.dokument, zustand.bereich),
          auswahl: ergebnis.auswahl,
          verlauf,
          // Eine Operation darf das Format der Schreibmarke über einen Absatz-/Zeilenwechsel weiterreichen
          // (`formatWeiterfuehren`); sonst beginnt die nächste Eingabe ohne ausstehendes Format.
          ausstehendesFormat: ergebnis.ausstehendesFormat ?? null,
          renderVersion,
        },
        aktion.fokus,
      );
    }
    case 'dokument': {
      if (aktion.dokument === zustand.dokument) return mitFokus(zustand, aktion.fokus);
      const dokument = bereichSichern(aktion.dokument, zustand.bereich);
      // Liegt die mitgegebene Auswahl in einem anderen Bereich als dem aktiven (Umbruch im Text
      // entfernen, während Kopf- oder Fußzeile bearbeitet wird), bleibt die Schreibmarke im aktiven
      // Bereich; die Textstelle wird für die Rückkehr in den Text gemerkt (Kollegenreview !751).
      const geliefert = aktion.auswahl;
      const imAnderenBereich = !!geliefert && geliefert.anker.bereich !== zustand.bereich;
      const auswahl =
        geliefert !== undefined && !imAnderenBereich
          ? geliefert && auswahlKlemmen(dokument, geliefert)
          : zustand.auswahl && auswahlKlemmen(dokument, zustand.auswahl);
      const letzteTextauswahl =
        geliefert && imAnderenBereich && geliefert.anker.bereich === 'text'
          ? auswahlKlemmen(dokument, geliefert)
          : zustand.letzteTextauswahl;
      const verlauf = aufnehmen(zustand.verlauf, schnappschuss(zustand), aktion.art, aktion.zeitMs, auswahl);
      return mitFokus({ ...zustand, dokument, auswahl, letzteTextauswahl, verlauf }, aktion.fokus);
    }
    case 'rueckgaengig': {
      const schritt = rueckgaengig(zustand.verlauf, schnappschuss(zustand));
      return schritt ? verlaufsschritt(zustand, schritt) : zustand;
    }
    case 'wiederholen': {
      const schritt = wiederholen(zustand.verlauf, schnappschuss(zustand));
      return schritt ? verlaufsschritt(zustand, schritt) : zustand;
    }
    case 'bereich': {
      let { dokument } = zustand;
      if (zustand.bereich !== 'text' && zustand.bereich !== aktion.bereich) {
        dokument = leerenBereichAufraeumen(dokument, zustand.bereich);
      }
      dokument = bereichSichern(dokument, aktion.bereich);
      const letzteTextauswahl = zustand.bereich === 'text' ? zustand.auswahl : zustand.letzteTextauswahl;
      let auswahl: Auswahl | null;
      if (aktion.auswahl !== undefined) auswahl = aktion.auswahl;
      else if (aktion.bereich === 'text' && letzteTextauswahl) auswahl = auswahlKlemmen(dokument, letzteTextauswahl);
      else {
        const anfang = anfangVon(dokument, aktion.bereich);
        auswahl = anfang ? kollabiert(anfang) : null;
      }
      return mitFokus(
        {
          ...zustand,
          dokument,
          bereich: aktion.bereich,
          aktiveSeite: aktion.seite,
          auswahl,
          letzteTextauswahl,
          ausstehendesFormat: null,
        },
        aktion.fokus,
      );
    }
    case 'format': {
      if (!zustand.auswahl) return zustand;
      if (istKollabiert(zustand.auswahl)) {
        // Ohne Auswahl gilt das Merkmal für die nächste Eingabe (ausstehendes Format).
        const basis = zustand.ausstehendesFormat ?? formatAnAuswahl(zustand.dokument, zustand.auswahl);
        return formatwahlAufnehmen(zustand, { ...basis, [aktion.merkmal]: !basis[aktion.merkmal] }, aktion.zeitMs);
      }
      return editorReducer(zustand, {
        typ: 'operation',
        art: 'sonstiges',
        ausfuehren: (dokument, auswahl) => formatUmschalten(dokument, auswahl, aktion.merkmal),
        zeitMs: aktion.zeitMs,
        fokus: true,
      });
    }
    case 'groesse': {
      if (!zustand.auswahl) return zustand;
      if (istKollabiert(zustand.auswahl)) {
        // Ohne Markierung gilt die Größe für die nächste Eingabe (ausstehendes Format); die Grundgröße des
        // Bereichs heißt „keine eigene Größe“.
        const basis = zustand.ausstehendesFormat ?? formatAnAuswahl(zustand.dokument, zustand.auswahl);
        const eigene = eigeneGroesse(zustand.dokument, zustand.auswahl.anker.bereich, aktion.groessePt);
        return mitFokus(formatwahlAufnehmen(zustand, mitGroesse(basis, eigene), aktion.zeitMs), aktion.fokus);
      }
      return editorReducer(zustand, {
        typ: 'operation',
        art: 'sonstiges',
        ausfuehren: (dokument, auswahl) => groesseSetzen(dokument, auswahl, aktion.groessePt),
        zeitMs: aktion.zeitMs,
        fokus: aktion.fokus,
      });
    }
    case 'fokus':
      return mitFokus(zustand, true);
    case 'neuAufbauen':
      return { ...zustand, renderVersion: zustand.renderVersion + 1, fokusAnforderung: zustand.fokusAnforderung + 1 };
    case 'zuruecksetzen':
      return anfangszustand(aktion.dokument);
    default:
      return zustand;
  }
}

/**
 * Fügt Text ein; Zeilen (\n) werden zu neuen Absätzen, Tabulatoren zu Leerzeichen, Steuerzeichen entfallen.
 * Mit `mitErkennung` (Zwischenablage, Teilpaket 3) wird `{{KEY}}` mit bekanntem Schlüssel zum
 * unteilbaren Platzhalterknoten und seit RITA-1533 ☐/☒/☑ zu einem echten Kästchen; Text und Platzhalter
 * einer Zeile erhalten dabei dasselbe Format wie beim Einfügen ohne Erkennung (Kästchen tragen keins).
 */
export function textMitZeilenEinfuegen(
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  text: string,
  format: Formatmerkmale | undefined,
  mitErkennung = false,
): Bearbeitungsergebnis {
  const zeilen = text
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, ' ')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')
    .split('\n');
  let aktuell: Bearbeitungsergebnis = { dokument, auswahl };
  // Das Format der Einfügestelle steht VOR dem ersten Segment fest: das ausstehende Format (Fett vor dem
  // Einfügen) oder das geerbte. Ein führender Platzhalter ersetzt die Markierung zuerst; danach wäre deren
  // Format nicht mehr zu ermitteln (Kollegenreview !756). Es gilt für ALLE Zeilen (RITA-1534, Entscheidung
  // Julia 09.10.2026): Zeile 2 ff. stehen in neuen Absätzen, deren Anfang sonst kein Format (leerer
  // Folgeabsatz am Blockende) oder das des Restlaufs mitten im Block hätte – wie in Word bekommt der ganze
  // eingefügte Text Größe und Format der Einfügestelle.
  const zeilenformat = format ?? einfuegeformat(dokument, auswahl);
  zeilen.forEach((zeile, index) => {
    if (index > 0) aktuell = blockTeilen(aktuell.dokument, aktuell.auswahl);
    const segmente: Textsegment[] = mitErkennung ? textInSegmente(zeile) : [{ typ: 'text', text: zeile }];
    segmente.forEach((segment) => {
      if (segment.typ === 'platzhalter') {
        aktuell = knotenWieTextEinfuegen(aktuell.dokument, aktuell.auswahl, [
          platzhalter(segment.schluessel, zeilenformat),
        ]);
      } else if (segment.typ === 'kaestchen') {
        aktuell = knotenWieTextEinfuegen(aktuell.dokument, aktuell.auswahl, [kaestchen(segment.angekreuzt)]);
      } else if (segment.text !== '') {
        aktuell = textEinfuegen(aktuell.dokument, aktuell.auswahl, segment.text, zeilenformat);
      }
    });
  });
  return aktuell;
}

/**
 * Führt das Format der Schreibmarke über einen Absatz- (Enter), Zeilen- (Umschalt+Enter) oder Seitenumbruch weiter
 * (RITA-1534, Kollegenreview !768): Steht die Schreibmarke danach an einer Stelle ohne Formatträger (leerer neuer
 * Absatz, Zeilenende), erbte die nächste Eingabe sonst nichts, und die gewählte bzw. geerbte Größe – ebenso
 * Fett/Kursiv/Unterstrichen – ginge verloren. Dann wird das Format der Ausgangsstelle (ausstehendes oder
 * geerbtes) als ausstehendes Format mitgegeben; hat die neue Stelle einen Formatträger (Enter mitten im Text),
 * gilt wie bisher dessen Format. Bleibt das Dokument unverändert, bleibt auch das Ergebnis unverändert.
 */
export function formatWeiterfuehren(
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  ausstehend: Formatmerkmale | null,
  operation: (dokument: Vertragsdokument, auswahl: Auswahl) => Bearbeitungsergebnis,
): Operationsergebnis {
  const format = ausstehend ?? einfuegeformat(dokument, auswahl);
  const ergebnis = operation(dokument, auswahl);
  if (ergebnis.dokument === dokument) return ergebnis;
  if (formateGleich(format, OHNE_FORMAT)) return ergebnis;
  if (geerbtesFormatVorPosition(ergebnis.dokument, ergebnis.auswahl.fokus) !== null) return ergebnis;
  return { ...ergebnis, ausstehendesFormat: format };
}

/**
 * Knoten einfügen (Platzhalterleiste): Ein Platzhalter erhält wie getippter Text das ausstehende Format
 * bzw. das Format der Einfügestelle, ermittelt VOR dem Ersetzen der Markierung, und ersetzt die
 * Markierung mit denselben Auswahlregeln wie Tippen – eine mitmarkierte Leerzeile bleibt (RITA-1533, V11).
 */
export function knotenEinfuegenOperation(knoten: Inlineknoten[]): Operation {
  return (dokument, auswahl, ausstehend) =>
    knotenWieTextEinfuegen(
      dokument,
      auswahl,
      mitEinfuegeformat(knoten, ausstehend ?? einfuegeformat(dokument, auswahl)),
    );
}

/** Kästchen-Knopf: ein leeres Kästchen, mit denselben Auswahlregeln wie Tippen (RITA-1533, V11). */
export const kaestchenEinfuegenOperation: Operation = (dokument, auswahl) =>
  knotenWieTextEinfuegen(dokument, auswahl, [kaestchen(false)]);

export interface Editorbefehle {
  /** Tippen (art „tippen“) oder Einfügen/Ersetzen (art „sonstiges“), optional an einer anderen Auswahl. */
  textEinfuegen: (text: string, auswahl?: Auswahl, art?: Schrittart) => void;
  /** Reiner Text aus der Zwischenablage: Zeilen werden Absätze, `{{KEY}}` bekannte Platzhalter (ein Verlaufsschritt). */
  textAusZwischenablageEinfuegen: (text: string) => void;
  knotenEinfuegen: (knoten: Inlineknoten[]) => void;
  zeilenumbruch: () => void;
  absatzTeilen: () => void;
  rueckwaerts: () => void;
  vorwaerts: () => void;
  auswahlLoeschen: (auswahl?: Auswahl) => void;
  auswahlSetzen: (auswahl: Auswahl | null) => void;
  allesAuswaehlen: () => void;
  format: (merkmal: Formatmerkmal) => void;
  /** Schriftgröße in Punkt für die Markierung bzw. die nächste Eingabe (RITA-1534); holt die Schreibmarke zurück. */
  groesse: (groessePt: number) => void;
  /**
   * Grundgröße des ganzen Vertrags (Verlaufsschritt; die Fußzeile behält ihre feste Größe). Holt den Fokus nicht
   * zurück: Das Fenster hält ihn noch, das Auswahlfeld fordert ihn nach dem Ausblenden an (`fokusAnfordern`).
   */
  grundgroesse: (groessePt: number) => void;
  /** Zeilenabstand des ganzen Vertrags samt Kopf- und Fußzeile (RITA-1571, Verlaufsschritt; Werte in model/abstaende.ts). */
  zeilenabstand: (zeilenhoehe: number) => void;
  /** Abstand nach Absatz des ganzen Vertrags in em (RITA-1571, Verlaufsschritt); der wirksame Wert ändert nichts. */
  absatzabstand: (absatzabstandEm: number) => void;
  /** Holt Fokus und Schreibmarke in den Vertrag zurück, ohne etwas zu ändern (nach Menü oder Fenster). */
  fokusAnfordern: () => void;
  liste: () => void;
  seitenumbruch: () => void;
  seitenumbruchEntfernen: (blockId: string) => void;
  kaestchenEinfuegen: () => void;
  kaestchenUmschalten: (bereich: Bereich, blockId: string, eintrag: number, knotenIndex: number) => void;
  /** Start merkt den Zustand und entfernt eine Auswahl nur vorläufig, ohne den Verlauf zu ändern. */
  kompositionStarten: (auswahl: Auswahl | null) => void;
  /** Leerer Text bricht ab; bestätigter Text ersetzt die ursprüngliche Auswahl als ein Verlaufsschritt. */
  kompositionAbschliessen: (text: string) => void;
  rueckgaengig: () => void;
  wiederholen: () => void;
  bereichWechseln: (bereich: Bereich, seite?: number, auswahl?: Auswahl | null) => void;
  logo: (logo: Partial<Logoeinstellung>) => void;
  seitenzahl: (seitenzahl: Partial<{ anzeigen: boolean; position: HorizontalePosition }>) => void;
  zuruecksetzen: (dokument: Vertragsdokument) => void;
}

export interface VertragEditorApi {
  zustand: Editorzustand;
  dispatch: React.Dispatch<Editoraktion>;
  befehle: Editorbefehle;
  /**
   * Liest die DOM-Auswahl synchron ins Modell (setzt der Editor). Die Werkzeugleiste ruft das vor
   * jedem Befehl auf, weil `selectionchange` asynchron eintrifft.
   */
  auswahlLeser: MutableRefObject<(() => void) | null>;
  /** Format am Caret bzw. das ausstehende Format für die nächste Eingabe. */
  formatAktuell: Formatmerkmale;
  /** Wirksame Schriftgröße in Punkt am Caret, der Markierung bzw. der nächsten Eingabe; null = gemischt (RITA-1534). */
  groesseAktuell: number | null;
  kannRueckgaengig: boolean;
  kannWiederholen: boolean;
}

export function useVertragEditor(anfangsdokument: Vertragsdokument): VertragEditorApi {
  const [zustand, dispatch] = useReducer(editorReducer, anfangsdokument, anfangszustand);
  const auswahlLeser = useRef<(() => void) | null>(null);

  const operation = useCallback(
    (art: Schrittart, ausfuehren: Operation, fokus = false, neuAufbau = false, verschmelzen = false) =>
      dispatch({ typ: 'operation', art, ausfuehren, zeitMs: Date.now(), fokus, neuAufbau, verschmelzen }),
    [],
  );

  // Dokumentbefehle (Logo, Seitenzahl, Kästchen, Umbruch entfernen) dürfen nicht aus dem
  // vorläufig gekürzten Kompositionsdokument gebaut werden. Der Reducer bricht vorher ab.
  const befehlszustand = zustand.komposition?.vorher ?? zustand;
  const befehle = useMemo<Editorbefehle>(
    () => ({
      textEinfuegen: (text, auswahl, art = 'tippen') =>
        operation(art, (dokument, aktuelle, ausstehend) =>
          textMitZeilenEinfuegen(dokument, auswahl ?? aktuelle, text, ausstehend ?? undefined),
        ),
      textAusZwischenablageEinfuegen: (text) =>
        operation('sonstiges', (dokument, aktuelle, ausstehend) =>
          textMitZeilenEinfuegen(dokument, aktuelle, text, ausstehend ?? undefined, true),
        ),
      knotenEinfuegen: (knoten) => operation('sonstiges', knotenEinfuegenOperation(knoten), true),
      zeilenumbruch: () =>
        operation('sonstiges', (dokument, auswahl, ausstehend) =>
          formatWeiterfuehren(dokument, auswahl, ausstehend, zeilenumbruchEinfuegen),
        ),
      absatzTeilen: () =>
        operation('sonstiges', (dokument, auswahl, ausstehend) =>
          formatWeiterfuehren(dokument, auswahl, ausstehend, blockTeilen),
        ),
      rueckwaerts: () => operation('loeschen', loescheRueckwaerts),
      vorwaerts: () => operation('loeschen', loescheVorwaerts),
      auswahlLoeschen: (auswahl) =>
        operation('loeschen', (dokument, aktuelle) => loescheAuswahl(dokument, auswahl ?? aktuelle)),
      auswahlSetzen: (auswahl) => dispatch({ typ: 'auswahl', auswahl }),
      allesAuswaehlen: () => {
        const anfang = anfangVon(befehlszustand.dokument, befehlszustand.bereich);
        const ende = endeVon(befehlszustand.dokument, befehlszustand.bereich);
        if (anfang && ende) dispatch({ typ: 'auswahl', auswahl: { anker: anfang, fokus: ende } });
      },
      format: (merkmal) => dispatch({ typ: 'format', merkmal, zeitMs: Date.now() }),
      groesse: (groessePt) => dispatch({ typ: 'groesse', groessePt, zeitMs: Date.now(), fokus: true }),
      grundgroesse: (groessePt) => {
        const { dokument } = befehlszustand;
        if (groessePt === dokument.schrift.groessePt) return;
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: { ...dokument, schrift: { ...dokument.schrift, groessePt } },
          zeitMs: Date.now(),
        });
      },
      zeilenabstand: (zeilenhoehe) => {
        const { dokument } = befehlszustand;
        if (zeilenhoehe === dokument.schrift.zeilenhoehe) return;
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: { ...dokument, schrift: { ...dokument.schrift, zeilenhoehe } },
          zeitMs: Date.now(),
        });
      },
      absatzabstand: (absatzabstandEm) => {
        const { dokument } = befehlszustand;
        // Wer bei einer alten Vorlage „klein“ (= bisheriger Abstand) wählt, ändert nichts: kein Verlaufsschritt,
        // keine ungespeicherte Änderung.
        if (absatzabstandEm === absatzabstandEmVon(dokument.schrift)) return;
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: { ...dokument, schrift: { ...dokument.schrift, absatzabstandEm } },
          zeitMs: Date.now(),
        });
      },
      fokusAnfordern: () => dispatch({ typ: 'fokus' }),
      liste: () => operation('sonstiges', listeUmschalten, true),
      seitenumbruch: () =>
        operation(
          'sonstiges',
          (dokument, auswahl, ausstehend) => formatWeiterfuehren(dokument, auswahl, ausstehend, seitenumbruchEinfuegen),
          true,
        ),
      seitenumbruchEntfernen: (blockId) => {
        // Die Schreibposition kommt aus dem Modell und gehört zum Dokument NACH dem Entfernen: Beim
        // Zusammenführen zweier Aufzählungen entfällt eine Kennung (Kollegenreview !751).
        const ergebnis = seitenumbruchEntfernen(befehlszustand.dokument, blockId);
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: ergebnis.dokument,
          auswahl: ergebnis.auswahl ?? undefined,
          zeitMs: Date.now(),
          fokus: true,
        });
      },
      kaestchenEinfuegen: () => operation('sonstiges', kaestchenEinfuegenOperation, true),
      kaestchenUmschalten: (bereich, blockId, eintrag, knotenIndex) =>
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: kaestchenUmschalten(befehlszustand.dokument, bereich, blockId, eintrag, knotenIndex),
          zeitMs: Date.now(),
        }),
      kompositionStarten: (auswahl) => dispatch({ typ: 'kompositionStart', auswahl }),
      kompositionAbschliessen: (text) => dispatch({ typ: 'kompositionEnde', text, zeitMs: Date.now() }),
      rueckgaengig: () => dispatch({ typ: 'rueckgaengig' }),
      wiederholen: () => dispatch({ typ: 'wiederholen' }),
      bereichWechseln: (bereich, seite, auswahl) =>
        dispatch({ typ: 'bereich', bereich, seite: seite ?? befehlszustand.aktiveSeite, auswahl, fokus: true }),
      logo: (logo) =>
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: {
            ...befehlszustand.dokument,
            kopf: { ...befehlszustand.dokument.kopf, logo: { ...befehlszustand.dokument.kopf.logo, ...logo } },
          },
          zeitMs: Date.now(),
        }),
      seitenzahl: (seitenzahl) =>
        dispatch({
          typ: 'dokument',
          art: 'sonstiges',
          dokument: {
            ...befehlszustand.dokument,
            fuss: {
              ...befehlszustand.dokument.fuss,
              seitenzahl: { ...befehlszustand.dokument.fuss.seitenzahl, ...seitenzahl },
            },
          },
          zeitMs: Date.now(),
        }),
      zuruecksetzen: (dokument) => dispatch({ typ: 'zuruecksetzen', dokument }),
    }),
    [operation, befehlszustand],
  );

  const formatAktuell = useMemo(() => {
    if (zustand.ausstehendesFormat) return zustand.ausstehendesFormat;
    return zustand.auswahl ? formatAnAuswahl(zustand.dokument, zustand.auswahl) : OHNE_FORMAT;
  }, [zustand.ausstehendesFormat, zustand.auswahl, zustand.dokument]);

  // Ohne Auswahl (direkt nach dem Laden) zeigt das Auswahlfeld die Grundgröße des aktiven Bereichs.
  const groesseAktuell = useMemo(() => {
    const { dokument, bereich, auswahl, ausstehendesFormat } = zustand;
    if (ausstehendesFormat) return ausstehendesFormat.groessePt ?? grundgroesseVon(dokument, bereich);
    return auswahl ? groesseAnAuswahl(dokument, auswahl) : grundgroesseVon(dokument, bereich);
  }, [zustand]);

  return {
    zustand,
    dispatch,
    befehle,
    auswahlLeser,
    formatAktuell,
    groesseAktuell,
    kannRueckgaengig: kannRueckgaengig(zustand.verlauf),
    kannWiederholen: kannWiederholen(zustand.verlauf),
  };
}

export default useVertragEditor;
